import React from 'react'
import { CloseCircle } from 'iconsax-react'
import { ClickAwayListener, Popper, useTheme } from '@mui/material'
import { format } from 'date-fns'

import Button from '@SonarCX/components/atoms/Button2'
import MediaAvatar from '@SonarCX/components/atoms/MediaAvatar'
import ModalTextField from '@SonarCX/components/atoms/ModalTextField'
import RedButton from '@SonarCX/components/atoms/RedButton'
import BaseModal from '@SonarCX/components/molecules/ChatModals/BaseModal'
import useDelayedUserSearch, {
  WITHIN_USERS
} from '@SonarCX/hooks/useDelayedUserSearch/v2'
import Result from '@SonarCX/components/organisms/ChatWindowV2/Header/EditParticipantsModal/Result'
import Tooltip from '@SonarCX/components/atoms/Tooltip'

import { useAppDispatch } from '@SonarCX/hooks/redux'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { uniqueArray } from '@SonarCX/utilities'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { createTeam, updateTeam } from '@SonarCX/slices/teamsSlice'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'

import useStyles from './styles'

const BACKSPACE = 'Backspace'
const MEMBERS = 'members'
const LEADERS = 'leaders'
const INITIAL_VALUES = {
  id: '',
  name: '',
  desc: '',
  members: [],
  leaders: [],
  created_at: '',
  updated_at: ''
}

const formatDataforApi = data => {
  const newObj = { ...data }
  delete newObj.id
  delete newObj.created_at
  delete newObj.updated_at
  return newObj
}

const checkIfDataIsValid = data => {
  if (data.members.length === 0 || data.name === '' || data.desc === '')
    return true

  return false
}

const ConditionalWrapper = ({ condition, wrapper, children }) => {
  return condition ? wrapper(children) : children
}

const TeamsEditModal = ({ open, onClose, data }) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const [values, setValues] = React.useState(data ?? INITIAL_VALUES)
  const [searchVal, setSearchVal] = React.useState('')
  const [searchTeamLeadVal, setSearchTeamLeadVal] = React.useState('')
  const [changed, setChanged] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [leadMenuOpen, setLeadMenuOpen] = React.useState(false)
  const { search, results, noResults, setNoResults } =
    useDelayedUserSearch(false)
  const {
    search: searchLead,
    results: resultsLead,
    noResults: noLeaderResults,
    setNoResults: setNoLeaderResults
  } = useDelayedUserSearch(false, WITHIN_USERS(values.members))
  const { allUsers, loadUser, updateUsers } = useUserAPI()
  const { setAction, setReason, setV2 } = useAreYouSure()

  const anchorRef = React.useRef(null)
  const leadAnchorRef = React.useRef(null)

  const classes = useStyles()

  // Show results when search is valid
  React.useEffect(() => {
    const showMenu =
      ((results && results.length > 0) || noResults) && searchVal.length > 2
    setMenuOpen(showMenu)
  }, [results, searchVal, noResults])

  // Show leader results when search is valid
  React.useEffect(() => {
    const showMenu =
      ((resultsLead && resultsLead.length > 0) || noLeaderResults) &&
      searchTeamLeadVal.length > 2
    setLeadMenuOpen(showMenu)
  }, [resultsLead, searchTeamLeadVal, noLeaderResults])

  //Reset no results
  React.useEffect(() => {
    if (searchVal.length <= 2 && noResults) {
      setNoResults(false)
    }
    //eslint-disable-next-line
  }, [searchVal])

  //Reset no results
  React.useEffect(() => {
    if (searchTeamLeadVal.length <= 2 && noLeaderResults) {
      setNoLeaderResults(false)
    }
    //eslint-disable-next-line
  }, [searchTeamLeadVal])

  React.useEffect(() => {
    updateUsers(results)
  }, [updateUsers, results])

  // Updates local values if editing
  React.useEffect(() => {
    const editing = Boolean(data)
    if (editing) {
      const notFound = []
      data?.members.forEach(id => {
        if (!allUsers[id]) {
          notFound.push(id)
        }
      })
      loadUser(notFound)
    }
    setEditing(editing)
    //eslint-disable-next-line
  }, [data])

  const removeAgent = React.useCallback((prop, id) => {
    setChanged(true)
    setValues(old => {
      if (prop === MEMBERS)
        //if members is part of leaders, remove leader as well
        return {
          ...old,
          [prop]: old[prop].filter(x => x !== id),
          leaders: old.leaders.filter(x => x !== id)
        }

      return { ...old, [prop]: old[prop].filter(x => x !== id) }
    })
  }, [])

  const handleSaveClick = React.useCallback(async () => {
    try {
      setSaving(true)

      const data = { ...values }
      if (editing) {
        let id = data.id
        await dispatch(updateTeam(id, formatDataforApi(data)))
      } else {
        await dispatch(createTeam(formatDataforApi(data)))
      }
      dispatch(AddSuccessAlert('Changes saved'))
      onClose()
    } catch (err) {
      console.error('Error saving team', err)
      dispatch(AddErrorAlert(err))
    } finally {
      setSaving(false)
    }
  }, [editing, values, onClose, dispatch])

  const handleKeyDown = React.useCallback(
    (e, prop) => {
      if (
        e.key === BACKSPACE &&
        e.target.value.length === 0 &&
        values[prop].length > 0
      ) {
        e.preventDefault()
        // Remove last id from list
        if (prop === MEMBERS) {
          const member = values.members[values.members.length - 1]
          setValues(old => ({ ...old, [prop]: old[prop].slice(0, -1) }))
          removeAgent(MEMBERS, member)
          return
        }
        setValues(old => ({ ...old, [prop]: old[prop].slice(0, -1) }))
      }
    },
    [values, removeAgent]
  )

  return (
    <>
      {/* @ts-ignore */}
      <BaseModal
        open={open}
        showTitleOnMobile={true}
        onClose={() => {
          const action = onClose

          if (changed) {
            setReason('You have unsaved changes')
            setAction(action)
            setV2(true)
          } else {
            action()
          }
        }}
        title={`${editing ? 'Edit' : 'Add'} Team`}
        PaperProps={{ style: { width: '50%' } }}
      >
        <ModalTextField
          title='Name *'
          toolTipText='required'
          className={classes.input}
        >
          <input
            placeholder='Team name'
            defaultValue={values.name}
            onBlur={e => {
              const val = e.target.value
              if (val !== values.name) {
                setChanged(true)
              }
              setValues(old => ({ ...old, name: val }))
            }}
          />
        </ModalTextField>
        <ModalTextField
          title='Description *'
          toolTipText='required'
          className={classes.multiLine}
        >
          <textarea
            placeholder='Team description'
            defaultValue={values.desc}
            onBlur={e => {
              const val = e.target.value
              if (val !== values.desc) {
                setChanged(true)
              }
              setValues(old => ({ ...old, desc: val }))
            }}
          />
        </ModalTextField>
        <section ref={anchorRef} className={classes.userSearchSection}>
          <Tooltip arrow title='required'>
            <div>Members *</div>
          </Tooltip>
          <div>
            {values.members.map(id => {
              const user = allUsers[id]
              const isDeleted = Boolean(user?.deleted_at)
              return (
                <ConditionalWrapper
                  key={`members-${id}`}
                  condition={isDeleted}
                  wrapper={children => (
                    <Tooltip
                      title={`User deleted at ${format(
                        new Date(user?.deleted_at ?? new Date()),
                        'MMM d, h:mm aaa'
                      )}`}
                    >
                      {children}
                    </Tooltip>
                  )}
                >
                  <div
                    onClick={e => e.stopPropagation()}
                    className={classes.chip}
                  >
                    <MediaAvatar user={user} className={classes.chipAvatar} />

                    <label className={isDeleted && classes.deletedUser}>
                      {user?.name || '...'}
                    </label>

                    <CloseCircle
                      size={theme.size.base}
                      className={classes.chipAction}
                      onClick={() => {
                        removeAgent(MEMBERS, id)
                      }}
                    />
                  </div>
                </ConditionalWrapper>
              )
            })}

            <input
              placeholder='Team members'
              value={searchVal}
              className={classes.memberInput}
              onChange={e => {
                // Update value and lookup users
                const val = e.target.value
                setSearchVal(val)
                search(val)
              }}
              onKeyDown={e => {
                handleKeyDown(e, MEMBERS)
              }}
            />
            {menuOpen && (
              <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                <Popper
                  open={menuOpen}
                  disablePortal={false}
                  anchorEl={anchorRef?.current}
                  style={{
                    zIndex: '1500',
                    width: `${
                      anchorRef.current?.getBoundingClientRect()?.width ?? 0
                    }px`
                  }}
                  className={classes.resultsContainer}
                  placement='bottom-start'
                >
                  <div className={classes.resultsTitle}>Results</div>

                  <div className={classes.results}>
                    {noResults && (
                      <div className={classes.noResult}>No results</div>
                    )}
                    {results.map(user => (
                      <div
                        key={`member-results-${user.id}`}
                        onClick={() => {
                          // Add person to list
                          setValues(old => {
                            const prevMembers = [...old.members]
                            const newMembers = uniqueArray(old.members, user.id)
                            if (newMembers.length === prevMembers.length) {
                              return old
                            }
                            setChanged(true)

                            return {
                              ...old,
                              members: newMembers
                            }
                          })
                          // Reset state
                          setSearchVal('')
                          setMenuOpen(false)
                          search('')
                        }}
                        className={classes.result}
                      >
                        <Result user={user} />
                      </div>
                    ))}
                  </div>
                </Popper>
              </ClickAwayListener>
            )}
          </div>
        </section>
        <section ref={leadAnchorRef} className={classes.userSearchSection}>
          <div>Leaders</div>
          <div>
            {values.leaders.map(id => {
              const user = allUsers[id]
              const isDeleted = Boolean(user?.deleted_at)

              return (
                <ConditionalWrapper
                  key={`leaders-${id}`}
                  condition={isDeleted}
                  wrapper={children => (
                    <Tooltip
                      title={`User deleted at ${format(
                        new Date(user?.deleted_at ?? new Date()),
                        'MMM d, h:mm aaa'
                      )}`}
                    >
                      {children}
                    </Tooltip>
                  )}
                >
                  <div
                    onClick={e => e.stopPropagation()}
                    className={classes.chip}
                  >
                    <MediaAvatar user={user} className={classes.chipAvatar} />

                    <label className={isDeleted && classes.deletedUser}>
                      {user?.name || '...'}
                    </label>

                    <CloseCircle
                      size={theme.size.base}
                      className={classes.chipAction}
                      onClick={() => {
                        removeAgent(LEADERS, id)
                      }}
                    />
                  </div>
                </ConditionalWrapper>
              )
            })}

            <input
              placeholder='Team leaders'
              value={searchTeamLeadVal}
              className={classes.memberInput}
              onChange={e => {
                // Update value and lookup users
                const val = e.target.value
                setSearchTeamLeadVal(val)
                searchLead(val)
              }}
              onKeyDown={e => {
                handleKeyDown(e, LEADERS)
              }}
            />
            {leadMenuOpen && (
              <ClickAwayListener onClickAway={() => setLeadMenuOpen(false)}>
                <Popper
                  open={leadMenuOpen}
                  disablePortal={false}
                  anchorEl={leadAnchorRef?.current}
                  style={{
                    zIndex: '1500',
                    width: `${
                      leadAnchorRef.current?.getBoundingClientRect()?.width ?? 0
                    }px`
                  }}
                  className={classes.resultsContainer}
                  placement='bottom-start'
                >
                  <div className={classes.resultsTitle}>Results</div>
                  {noLeaderResults && (
                    <div className={classes.noResult}>
                      No results (Leaders must be members of this group)
                    </div>
                  )}
                  <div className={classes.results}>
                    {resultsLead.map(user => (
                      <div
                        key={`leader-results-${user.id}`}
                        onClick={() => {
                          setChanged(true)

                          // Add person to list
                          setValues(old => ({
                            ...old,
                            leaders: uniqueArray(old.leaders, user.id)
                          }))
                          // Reset state
                          setSearchTeamLeadVal('')
                          setLeadMenuOpen(false)
                        }}
                        className={classes.result}
                      >
                        <Result user={user} />
                      </div>
                    ))}
                  </div>
                </Popper>
              </ClickAwayListener>
            )}
          </div>
        </section>
        <div className={classes.endControls}>
          <RedButton
            onClick={() => {
              const action = onClose

              if (changed) {
                setReason('You have unsaved changes')
                setAction(action)
                setV2(true)
              } else {
                action()
              }
            }}
            saving={saving}
            disabled={saving}
            className={classes.button}
          >
            Cancel
          </RedButton>

          <Button
            onClick={handleSaveClick}
            loading={saving}
            disabled={checkIfDataIsValid(values) || saving}
            className={classes.button}
          >
            Save
          </Button>
        </div>
      </BaseModal>
    </>
  )
}

export default TeamsEditModal
